import { AddIcon, RemoveIcon } from '@diligentcorp/atlas-react-icons';
import { FormLabel, IconButton, InputAdornment, Stack } from '@mui/material';
import React, { ChangeEvent, ReactNode } from 'react';
import { StyledTextField } from '../text-field';

export type NumberFieldProps = {
  name?: string;
  label?: string;
  labelInfo?: ReactNode;
  value?: number | '';
  onChange: (value: number | '' | undefined) => void;
  allowEmpty?: boolean;
  incrementRate?: number;
  decrementRate?: number;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  actions?: ReactNode;
  'data-testid'?: string;
};

function clamp(value: number, min?: number, max?: number): number {
  let result = value;
  if (min !== undefined) result = Math.max(min, result);
  if (max !== undefined) result = Math.min(max, result);
  return result;
}

export function NumberField({
  name,
  label,
  labelInfo,
  required,
  value,
  onChange,
  allowEmpty = true,
  incrementRate = 1,
  decrementRate = 1,
  error,
  helperText,
  disabled,
  readOnly,
  placeholder,
  min,
  max,
  actions,
  'data-testid': dataTestId,
}: NumberFieldProps): React.ReactElement {
  const clampAndUpdate = (numValue: number): void => {
    onChange(clamp(numValue, min, max));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const val = e.target.value;
    if (val === '') {
      onChange(allowEmpty ? '' : min);
      return;
    }
    if (/^\d+$/.test(val)) {
      clampAndUpdate(Number(val));
    }
  };

  return (
    <Stack width='100%'>
      {label && (
        <FormLabel required={required} id={`${name}-label`}>
          {label}
        </FormLabel>
      )}

      {labelInfo && <FormLabel color='info'>{labelInfo}</FormLabel>}

      <Stack flexDirection='row'>
        <StyledTextField
          sx={{ flex: 1 }}
          value={value ?? ''}
          onChange={handleChange}
          data-testid={dataTestId}
          data-aria-labelledby={`${name}-label`}
          type='text'
          error={error}
          helperText={helperText}
          disabled={disabled}
          placeholder={placeholder}
          inputProps={{
            'data-testid': `${dataTestId}-input`,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={() => {
                    clampAndUpdate(Number(value || min || 0) - decrementRate);
                  }}
                  aria-label='decrease number'
                  disabled={readOnly || disabled}
                >
                  <RemoveIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    clampAndUpdate(Number(value || min || 0) + incrementRate);
                  }}
                  aria-label='increase number'
                  disabled={readOnly || disabled}
                >
                  <AddIcon />
                </IconButton>
              </InputAdornment>
            ),
            readOnly,
          }}
        />
        {actions}
      </Stack>
    </Stack>
  );
}
